/**
 * TdsChallanData — what the e-filing portal says it was paid.
 *
 * Teamlio's own status says what somebody MEANT to do. The portal says what
 * happened, and it is the only place carrying the BSR code and the challan
 * serial — the two things a 24Q cannot be filed without.
 *
 * Nothing on this screen creates a challan, and that is the point rather
 * than an omission. Startup Khata had an import that built challans out of
 * portal rows and put five challans and 78,000 rupees into books that had
 * never raised them. Here a portal payment is reconciled by writing its BSR
 * code and serial onto a challan the branch actually raised — and only onto
 * one that could not be any other.
 *
 * M / U, the way the same rows read in Computax: M means one challan here
 * agrees with this payment on both its date and its total. It does NOT mean
 * anything has been written yet.
 */
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { tdsApi, PortalChallanRow } from "../../api/tds";

const CHALLAN_DATA_CSS = `
.cd-table { width: 100%; min-width: 980px; font-size: 12px; border-collapse: collapse; }
.cd-table th { text-align: left; padding: 8px; white-space: nowrap; background: var(--gt-surface-2); font-weight: 600; }
.cd-table td { padding: 8px; white-space: nowrap; border-top: 1px solid var(--gt-border); }
.cd-num { text-align: right; }
.cd-chip {
  display: inline-block; min-width: 22px; text-align: center;
  padding: 1px 6px; border-radius: 6px;
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.02em;
}
.cd-chip.is-m { background: #dcfce7; color: #14532d; }
.cd-chip.is-u { background: var(--gt-surface-2); color: var(--gt-text-tertiary); }
.cd-foot { font-size: 11.5px; color: var(--gt-text-secondary); margin: 10px 0 0; }
`;

const inr = (n: number) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-12" as "12 Oct 2026". Sliced rather than put through Date():
 *  an ISO date read as UTC comes back a day earlier in IST. */
function showDay(iso: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const month = MONTHS[Number(m) - 1];
  return month ? `${Number(d)} ${month} ${y}` : iso;
}

/** A figure the portal did not report is blank, not zero. "Nothing was
 *  said" and "it was nil" are different answers on a challan. */
const amountCell = (n: number) => (n ? inr(n) : "—");

// The columns Computax's OLTAS import screen shows, in its order. Kept in
// that order on purpose: the people reading this screen read the same rows
// in Computax, and a different order makes the two impossible to compare
// line by line.
//
// No SHE Cess column and no Assessment Year: the portal's payment payload
// carries no health cess field at all, and under the 2025 Act there is no
// assessment year -- it is the tax year, which is the financial year. A
// right number under a wrong heading is worse than no column.
const COLUMNS: Array<[string, (r: PortalChallanRow) => React.ReactNode, boolean]> = [
  ["Minor Head", (r) => r.minor_head || "—", false],
  ["Section", (r) => r.section || "—", false],
  ["Challan Date", (r) => showDay(r.tender_date) || "—", false],
  ["Challan No.", (r) => r.challan_number || "—", false],
  ["BSR Code", (r) => r.bsr_code || "—", false],
  ["TDS", (r) => amountCell(r.tds_amount), true],
  ["Surcharge", (r) => amountCell(r.surcharge_amount), true],
  ["Edu Cess", (r) => amountCell(r.edu_cess_amount), true],
  ["Interest", (r) => amountCell(r.interest_amount), true],
  ["Late Fee", (r) => amountCell(r.late_fee_amount), true],
  ["Penalty", (r) => amountCell(r.penalty_amount), true],
  ["Other", (r) => amountCell(r.other_amount), true],
  ["Total", (r) => amountCell(r.total_amount), true],
];

export default function TdsChallanData({
  financialYear,
  quarter,
  reloadKey,
  onImport,
  importing,
}: {
  financialYear: string;
  quarter: number;
  /** Bumped when a portal read finishes, so the table re-reads itself. */
  reloadKey?: number;
  onImport: () => void;
  importing?: boolean;
}) {
  const [rows, setRows] = useState<PortalChallanRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await tdsApi.portalChallans(financialYear, quarter);
      setRows(data.rows);
      setError(null);
    } catch (e: any) {
      setRows([]);
      setError(e?.response?.data?.detail || e?.message || "Couldn't read the stored portal challans.");
    } finally {
      setLoading(false);
    }
  }, [financialYear, quarter]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const record = async (row: PortalChallanRow) => {
    setRecording(row.id);
    try {
      const { data } = await tdsApi.recordPortalChallan(row.id);
      toast.success(data.updated.length
        ? `Recorded — BSR ${row.bsr_code}, serial ${row.challan_number}.`
        : "That challan already carried this receipt.");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not record that payment.");
    } finally {
      setRecording(null);
    }
  };

  const matched = useMemo(() => rows.filter((r) => r.match).length, [rows]);

  return (
    <div className="space-y-4">
      <style>{CHALLAN_DATA_CSS}</style>

      <div className="gt-card p-5" style={{ display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ maxWidth: 640 }}>
          <span className="gt-eyebrow">Challan Data</span>
          <p className="gt-page-sub" style={{ fontSize: 12.5, margin: "6px 0 0" }}>
            What the e-filing portal was paid for {financialYear} Q{quarter}.
          </p>
        </div>
        <button type="button" className="gt-action-btn"
          disabled={importing}
          title="Sign in to the e-filing portal, read its payment history and bring the salary challans here."
          onClick={onImport}>
          {importing ? "Importing…" : "Import challan data"}
        </button>
      </div>

      {error && (
        <div className="gt-card p-4" style={{ fontSize: 12, color: "var(--gt-danger, #b91c1c)" }}>
          {error}
        </div>
      )}

      <div className="gt-card p-0">
        <div style={{ overflow: "auto" }}>
          <table className="cd-table">
            <thead>
              <tr>
                {COLUMNS.map(([label, , num]) => (
                  <th key={label} className={num ? "cd-num" : ""}>{label}</th>
                ))}
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={COLUMNS.length + 2}
                    style={{ padding: 22, textAlign: "center", color: "var(--gt-text-tertiary)" }}>
                    {loading
                      ? "Loading…"
                      : importing
                        ? "Reading the portal…"
                        : `Nothing read yet for ${financialYear} Q${quarter} — use Import challan data.`}
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id}>
                  {COLUMNS.map(([label, cell, num]) => (
                    <td key={label} className={num ? "cd-num" : ""}>{cell(row)}</td>
                  ))}
                  <td>
                    {/* M means one challan here agrees on the date and the
                        total. It does not mean anything has been written. */}
                    <span className={`cd-chip ${row.match ? "is-m" : "is-u"}`}
                      title={row.match
                        ? `Matches one challan here: ${inr(row.match.total)}${row.match.month ? `, month ${row.match.month}` : ""}`
                        : "No single challan here matches this payment on its date and total."}>
                      {row.match ? "M" : "U"}
                    </span>
                  </td>
                  <td className="cd-num">
                    {/* Disabled rather than hidden: "this cannot be recorded"
                        and "there is no such thing as recording" read very
                        differently to somebody looking for the button. */}
                    <button type="button" className="gt-action-btn is-ghost"
                      style={{ padding: "4px 12px", fontSize: 11.5 }}
                      disabled={!row.match || !row.has_receipt || recording === row.id}
                      title={!row.has_receipt
                        ? "This payment has no BSR code and serial yet — there is nothing to record."
                        : !row.match
                          ? "No single challan here matches it. Open the challan and enter the BSR code by hand."
                          : "Write this BSR code and serial onto the matching challan."}
                      onClick={() => record(row)}>
                      {recording === row.id ? "Recording…" : "Record"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {rows.length > 0 && (
        <p className="cd-foot">
          {rows.length} payment{rows.length === 1 ? "" : "s"} for {financialYear} Q{quarter}
          {"  ·  "}Matched {matched}
          {"  ·  "}Unmatched {rows.length - matched}
        </p>
      )}
    </div>
  );
}
